import React, { FC, ReactElement, useContext } from 'react'
import { IconName } from '../../Common/Components/Icon'
import { IconPopper } from '../../Common/Components/IconPopper/IconPopper'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import LanguageContext from '../../Common/Contexts/LanguageContext'
import { UsersByExtension } from '../../../../i18n'
import { LicenceResponseMapped } from '../../../NonReact/Types/Types'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { SHOW_LICENCE_SUMMARY_FLAG } from '../../Common/Constants/FeatureFlags'

type Props = {
    licenceSummary: Nullable<LicenceResponseMapped>
    telSysType?: TelSysTypes
    shouldIncludeCXI: boolean
}

export const LicenceSummary: FC<Props> = ({ licenceSummary, telSysType, shouldIncludeCXI }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const showLicenceSummary: Definable<boolean> = useFeatureFlag(SHOW_LICENCE_SUMMARY_FLAG)

    return showLicenceSummary && licenceSummary ? (
        <div className='licence-summary'>
            <IconPopper className='ls-popper' iconName={IconName.LICENCES} placement='bottom'>
                <>
                    <div className='lsp-title'>{language.get(UsersByExtension.LicenceSummary_Title)}</div>

                    <div className='lsp-table-entry'>
                        <div className='lspte-left'>
                            <div className='lspte-header'>
                                {language.get(UsersByExtension.LicenceSummary_Headers_Type)}
                            </div>
                            <div className='lspte-entry'>
                                {language.getWithTelSysCheck(
                                    UsersByExtension.LicenceSummary_Types_Essentials,
                                    telSysType
                                )}
                            </div>
                            <div className='lspte-entry'>
                                {language.getWithTelSysCheck(
                                    UsersByExtension.LicenceSummary_Types_Enterprise,
                                    telSysType
                                )}
                            </div>
                            <div className='lspte-entry'>
                                {language.getWithTelSysCheck(
                                    UsersByExtension.LicenceSummary_Types_CallCentreAgent,
                                    telSysType
                                )}
                            </div>
                            <div className='lspte-entry'>
                                {language.getWithTelSysCheck(
                                    UsersByExtension.LicenceSummary_Types_CallCentreSupervisor,
                                    telSysType
                                )}
                            </div>
                            {shouldIncludeCXI ? (
                                <div className='lspte-entry'>
                                    {language.get(UsersByExtension.LicenceSummary_Types_CXIntegration)}
                                </div>
                            ) : null}
                            <div className='lspte-entry'>
                                {language.get(UsersByExtension.LicenceSummary_Types_Unlicenced)}
                            </div>
                        </div>
                        <div className='lspte-right'>
                            <div className='lspte-header'>
                                {language.get(UsersByExtension.LicenceSummary_Headers_InUse)}
                            </div>
                            <div className='lspte-entry'>
                                {licenceSummary?.UsedLicences?.Essentials ?? 0}
                                {(licenceSummary?.MaxLicences?.Essentials ?? 0) > 0
                                    ? `/${licenceSummary?.MaxLicences?.Essentials}`
                                    : ''}
                            </div>
                            <div className='lspte-entry'>
                                {licenceSummary?.UsedLicences?.Enterprise ?? 0}
                                {(licenceSummary?.MaxLicences?.Enterprise ?? 0) > 0
                                    ? `/${licenceSummary?.MaxLicences?.Enterprise}`
                                    : ''}
                            </div>
                            <div className='lspte-entry'>
                                {licenceSummary?.UsedLicences?.CallCentreAgent ?? 0}
                                {(licenceSummary?.MaxLicences?.CallCentreAgent ?? 0) > 0
                                    ? `/${licenceSummary?.MaxLicences?.CallCentreAgent}`
                                    : ''}
                            </div>
                            <div className='lspte-entry'>
                                {licenceSummary?.UsedLicences?.CallCentreSupervisor ?? 0}
                                {(licenceSummary?.MaxLicences?.CallCentreSupervisor ?? 0) > 0
                                    ? `/${licenceSummary?.MaxLicences?.CallCentreSupervisor}`
                                    : ''}
                            </div>
                            {shouldIncludeCXI ? (
                                <div className='lspte-entry'>
                                    {licenceSummary?.UsedLicences?.CXIntegration ?? 0}
                                    {(licenceSummary?.MaxLicences?.CXIntegration ?? 0) > 0
                                        ? `/${licenceSummary?.MaxLicences?.CXIntegration}`
                                        : ''}
                                </div>
                            ) : null}
                            <div className='lspte-entry'>{licenceSummary?.Unlicenced ?? 0}</div>
                        </div>
                    </div>
                </>
            </IconPopper>
        </div>
    ) : (
        <></>
    )
}
